import { NetworkStatus } from "@apollo/client";
import { useLocalStorage } from "@rehooks/local-storage";
import { isEqual } from "lodash-es";
import { useCallback, useMemo, useState } from "react";

import usePolledQuery from "apollo/usePolledQuery";
import useFiltersVariables from "components/Filters/useFiltersVariables";
import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import { SearchQueryRequestedPage } from "types/generated";
import { PollingIntervalGroups } from "apollo/constants";

import {
  TEMPLATES_LIMIT,
  TEMPLATES_LIMIT_STORAGE_KEY,
  FILTER_TYPE,
  initialSortDirection,
  initialSortOption,
} from "../constants";
import { SEARCH_TEMPLATE_VERSIONS } from "../gql";

type UseTemplatesArgs = {
  id: string;
};

const useTemplates = ({ id }: UseTemplatesArgs) => {
  const [rowsPerPage] = useLocalStorage<number>(TEMPLATES_LIMIT_STORAGE_KEY);
  const [requestedPage, setRequestedPage] = useState<SearchQueryRequestedPage | null>(null);
  const [after, setAfter] = useState<string | null>(null);

  const { onError } = useTypedContext(FlashContext);

  const { filterVariables, filterVariablesLoaded } = useFiltersVariables({
    filterType: FILTER_TYPE,
    initialSortDirection,
    initialSortOption,
  });

  const resetPaginationVariables = useMemo(() => {
    return {
      fullTextSearch: filterVariables.searchInput,
      predicates: filterVariables.predicates,
      ...(filterVariables.sortOptionFields && { orderBy: filterVariables.sortOptionFields }),
    };
  }, [filterVariables]);

  const paginationResetKey = useMemo(
    () => ({ id, ...resetPaginationVariables }),
    [id, resetPaginationVariables]
  );

  const [previousPaginationResetKey, setPreviousPaginationResetKey] = useState(paginationResetKey);

  if (!isEqual(previousPaginationResetKey, paginationResetKey)) {
    setPreviousPaginationResetKey(paginationResetKey);
    setRequestedPage(null);
    setAfter(null);
  }

  const initialVariables = useMemo(() => {
    return {
      id,
      input: {
        ...resetPaginationVariables,
        first: rowsPerPage || TEMPLATES_LIMIT,
        after,
        requestedPage,
      },
    };
  }, [resetPaginationVariables, rowsPerPage, after, requestedPage, id]);

  const skipQuery = !filterVariablesLoaded;

  const { error, loading, data, networkStatus, previousData, refetch } = usePolledQuery(
    SEARCH_TEMPLATE_VERSIONS,
    {
      variables: initialVariables,
      pollingGroup: PollingIntervalGroups.Lists,
      // avoid request executing twice while fetchMore
      nextFetchPolicy: "cache-first",
      // APOLLO CLIENT UPDATE
      skip: skipQuery,
    }
  );

  const loadTemplates = useCallback(
    async ({
      cursor,
      requestedPage,
    }: {
      cursor: string | null;
      rowsPerPage: number;
      requestedPage: SearchQueryRequestedPage | null;
    }) => {
      try {
        setAfter(cursor);
        setRequestedPage(requestedPage);
      } catch (error) {
        onError(error);
      }
    },
    [onError]
  );

  const initialLoading =
    (loading && networkStatus === NetworkStatus.loading && !data?.template) ||
    !filterVariablesLoaded;

  const loadingContent =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.fetchMore ||
      networkStatus === NetworkStatus.setVariables);

  const templates = useMemo(
    () =>
      data?.template?.searchTemplateVersions?.edges.map((edge) => edge.node) ||
      previousData?.template?.searchTemplateVersions?.edges.map((edge) => edge.node) ||
      [],
    [
      data?.template?.searchTemplateVersions?.edges,
      previousData?.template?.searchTemplateVersions?.edges,
    ]
  );

  return {
    refetch,
    loadTemplates,
    error,
    templates,
    initialLoading,
    noResults: !loading && !data?.template,
    endCursor: data?.template?.searchTemplateVersions?.pageInfo.endCursor,
    startCursor: data?.template?.searchTemplateVersions?.pageInfo.startCursor,
    isEmpty: data && !templates.length,
    loadingContent,
    predicates: filterVariables.predicates,
    paginationResetKey,
  };
};

export default useTemplates;
